import { Minus, Plus, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

interface StickerPrintControlsProps {
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onPrint: () => void;
  canPrint: boolean;
  isPrinting?: boolean;
  compact?: boolean;
}

export default function StickerPrintControls({
  quantity,
  onQuantityChange,
  onPrint,
  canPrint,
  isPrinting = false,
  compact = false,
}: StickerPrintControlsProps) {
  function decrement() {
    onQuantityChange(Math.max(1, quantity - 1));
  }

  function increment() {
    onQuantityChange(quantity + 1);
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1 w-full">
        <div className="flex items-center rounded-lg border border-border bg-white shrink-0">
          <button
            type="button"
            onClick={decrement}
            disabled={quantity <= 1 || isPrinting}
            className="size-8 flex items-center justify-center text-muted-foreground hover:bg-muted/50 disabled:opacity-30 rounded-l-lg"
            aria-label="Smanji broj naljepnica"
          >
            <Minus className="size-3.5" />
          </button>
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => onQuantityChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
            className="w-9 h-8 text-center text-xs font-semibold tabular-nums border-x border-border bg-transparent outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            aria-label="Broj naljepnica"
          />
          <button
            type="button"
            onClick={increment}
            disabled={isPrinting}
            className="size-8 flex items-center justify-center text-muted-foreground hover:bg-muted/50 disabled:opacity-30 rounded-r-lg"
            aria-label="Povećaj broj naljepnica"
          >
            <Plus className="size-3.5" />
          </button>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 flex-1 min-w-0"
          onClick={onPrint}
          disabled={!canPrint || isPrinting}
          title={canPrint ? "Štampaj SKU" : "Proizvod nema SKU"}
        >
          <Printer className="size-3.5" />
          <span className="hidden xl:inline">{isPrinting ? "..." : "Štampaj"}</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Broj naljepnica
      </p>
      <div className="flex items-center gap-2">
        <div className="flex items-center rounded-xl border border-border bg-white">
          <button
            type="button"
            onClick={decrement}
            disabled={quantity <= 1 || isPrinting}
            className="size-11 flex items-center justify-center text-muted-foreground hover:bg-muted/50 disabled:opacity-30 rounded-l-xl"
            aria-label="Smanji broj naljepnica"
          >
            <Minus className="size-4" />
          </button>
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => onQuantityChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
            className="w-14 h-11 text-center text-base font-bold tabular-nums border-x border-border bg-transparent outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            aria-label="Broj naljepnica"
          />
          <button
            type="button"
            onClick={increment}
            disabled={isPrinting}
            className="size-11 flex items-center justify-center text-muted-foreground hover:bg-muted/50 disabled:opacity-30 rounded-r-xl"
            aria-label="Povećaj broj naljepnica"
          >
            <Plus className="size-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={onPrint}
          disabled={!canPrint || isPrinting}
          className="flex-1 h-11 rounded-xl border border-border bg-white hover:bg-neutral-50 text-sm font-medium flex items-center justify-center gap-2 transition active:scale-[0.99] disabled:opacity-40"
        >
          <Printer className="size-4" />
          {isPrinting ? "Štampam..." : "Štampaj naljepnicu"}
        </button>
      </div>
    </div>
  );
}
